import React, { FC, ReactNode, useMemo, Fragment, useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { format, formatDuration, intervalToDuration } from 'date-fns';
import { Link } from 'react-router-dom';
import { Select, SelectChangeEvent, MenuItem } from '@mui/material';

import i18n from '../../../i18n';
import { StatusChip, StatusChipConfig } from '@thingslog/ui-components';
import { Device, DeviceIconDto, DeviceInitialConfig, RecordPeriod } from '@thingslog/repositories';
import DeviceDetailInput from './DeviceDetailInput';
import JwtValidator from '../../../common/JwtValidator';
import { DeviceLanguage } from '@thingslog/repositories/src/device/Device';

const DeviceDetails: FC<DeviceDetailsProps> = ({
  device,
  deviceInitialConfig,
  icons,
  timezones,
  onDeviceIconChange,
  onDeviceReplacementNumberChange,
  onDeviceTimezoneChange,
}: DeviceDetailsProps) => {
  const deviceIconId = useMemo(() => {
    return device?.deviceIconDto.id ?? 0;
  }, [device?.deviceIconDto.id]);
  const deviceTimezone = useMemo(() => {
    return device?.timezone;
  }, [device?.timezone]);
  const { t } = useTranslation();
  const jwtValidator = useMemo(() => new JwtValidator(), []);

  const handleReplacementNumberChange = (value: string | null): void => {
    onDeviceReplacementNumberChange(value);
  };

  const handleIconChange = (event: SelectChangeEvent): void => {
    let iconId: number;
    try {
      iconId = Number(event.target.value);
    } catch (e) {
      console.log('An error occurred while handling the icon change:', e);
      return;
    }
    onDeviceIconChange(iconId);
  };

  const handleTimezoneChange = (event: SelectChangeEvent): void => {
    const value = event.target.value;
    onDeviceTimezoneChange(value);
  };

  const deviceDetailsToDisplay: DeviceDetail[] = useMemo(() => {
    const convertRecordPeriodToSeconds = (time: number, recordPeriod: RecordPeriod): number => {
      let durationInSeconds: number | null;
      switch (recordPeriod) {
        case RecordPeriod.SECONDS:
          durationInSeconds = time;
          break;
        case RecordPeriod.MINUTES:
          durationInSeconds = time * 60;
          break;
        case RecordPeriod.HOURS:
          durationInSeconds = time * 60 * 60;
          break;
        case RecordPeriod.DAYS:
          durationInSeconds = time * 24 * 60 * 60;
          break;
        default:
          durationInSeconds = NaN;
          break;
      }
      return durationInSeconds;
    };

    const timezoneOptions = Array.from(timezones || []);

    const iconOptions: SelectOption[] = icons.map((icon: DeviceIconDto) => ({
      value: icon.id.toString(),
      label: (
        <>
          <img
            src={icon.icon}
            alt={icon.name}
            style={{ height: '2rem', width: 'auto', marginInlineEnd: '0.5rem' }}
          />
          <span>{icon.name}</span>
        </>
      ),
    }));

    const details: DeviceDetail[] = [
      {
        type: 'text',
        label: t('device_name'),
        value: device.name,
      },
      {
        type: 'text',
        label: t('device_number'),
        value: device.number,
      },
      {
        type: 'text',
        label: t('model'),
        value: device.model,
      },
      {
        type: 'text',
        label: t('nomenclature'),
        value: device.nomenclature || '',
      },
      {
        type: 'select',
        label: t('device_icon'),
        value: deviceIconId.toString(),
        options: iconOptions,
        onValueChange: handleIconChange,
      },
      {
        type: 'text',
        label: t('hardware_version'),
        value: device.hwVersion,
      },
      {
        type: 'text',
        label: t('software_version'),
        value: device.swVersion,
      },
      {
        type: 'text',
        label: t('description'),
        value: device.description || '',
      },
      {
        type: 'text',
        label: t('link_language'),
        value: device.language || DeviceLanguage.EN,
      },
      {
        type: 'text',
        label: t('manufacturing_date'),
        value: device.manufacturingDate
          ? format(new Date(device.manufacturingDate), 'dd MMM yyyy')
          : '',
      },
      {
        type: 'text',
        label: t('warranty_type'),
        value: device.extendedHardwareSupport
          ? t('warranty_extended_support')
          : t('warranty_basic'),
      },
      {
        type: 'status',
        label: t('warranty_status'),
        value: device.warranty?.status ?? 'UNKNOWN',
        config: {
          EXPIRED: { label: t('warranty_status_EXPIRED').toLocaleUpperCase(), color: 'error' },
          IN_WARRANTY: {
            label: t('warranty_status_IN_WARRANTY').toLocaleUpperCase(),
            color: 'info',
          },
          UNKNOWN: { label: t('warranty_status_UNKNOWN').toLocaleUpperCase(), color: 'warning' },
          EXTENDED_SUPPORT: {
            label: t('warranty_extended_support').toLocaleUpperCase(),
            color: 'default',
          },
        },
      },
      {
        type: 'text',
        label: t('install_date'),
        value: device.installDate ? format(new Date(device.installDate), 'dd MMM yyyy') : '',
      },
      {
        type: 'text',
        label: t('install_warranty_exp_date'),
        value: device.warranty?.installWarrantyExpirationDate
          ? format(new Date(device.warranty.installWarrantyExpirationDate), 'dd MMM yyyy')
          : '',
      },
      {
        type: 'period',
        label: t('transmission_period'),
        periodInSeconds: convertRecordPeriodToSeconds(
          deviceInitialConfig.transmissionSettings.countsThreshold *
            deviceInitialConfig.transmissionSettings.every,
          deviceInitialConfig.transmissionSettings.recordPeriod
        ),
      },
      {
        type: 'period',
        label: t('record_period'),
        periodInSeconds: convertRecordPeriodToSeconds(
          deviceInitialConfig.transmissionSettings.every,
          deviceInitialConfig.transmissionSettings.recordPeriod
        ),
      },
      {
        type: 'select',
        value: deviceTimezone || 'UTC',
        label: t('value_ranges_timezone'),
        options: timezoneOptions.map((timezone: string) => ({
          value: timezone,
          label: timezone,
        })),
        onValueChange: handleTimezoneChange,
      },
    ];

    if (jwtValidator.hasRole('ROLE_SUPER_ADMIN')) {
      if (device.replacedBy) {
        details.push({
          type: 'text',
          label: t('replaced_by'),
          value: device.replacedBy,
        });
      }

      details.push({
        type: 'input',
        label: t('replacement_number'),
        value: device.replacementNumber,
        onValueChange: handleReplacementNumberChange,
        requiresConfirmation: true,
      });
    }

    return details;
  }, [device, deviceInitialConfig, i18n.language, deviceIconId, deviceTimezone, timezones]);

  const linksToDisplay = useMemo(
    (): LinksToDisplay[] => [
      { linkText: t('link_readings'), linkUrl: `/app/DeviceCounters/${device.number}` },
      { linkText: t('link_graph'), linkUrl: `/app/Graph/${device.number}` },
      { linkText: t('link_flow'), linkUrl: `/app/DeviceCountersGraph/${device.number}` },
      { linkText: t('link_transmissions'), linkUrl: '/app/Transmissions' },
      { linkText: t('link_map'), linkUrl: `/app/DeviceLocation/${device.number}` },
    ],
    [i18n.language, device.number]
  );

  return (
    <div>
      <div className="flex-grow grid auto-rows-min grid-cols-[auto_1fr] gap-y-5 gap-x-10 text-left">
        {deviceDetailsToDisplay.map((deviceDetail: DeviceDetail) => {
          switch (deviceDetail.type) {
            case 'text':
              return (
                <Fragment key={deviceDetail.label}>
                  <div className="font-bold">{deviceDetail.label}</div>
                  <div className="break-words overflow-hidden">{deviceDetail.value}</div>
                </Fragment>
              );
            case 'period':
              return (
                <Fragment key={deviceDetail.label}>
                  <div className="font-bold">{deviceDetail.label}</div>
                  <div>
                    {deviceDetail.periodInSeconds
                      ? formatDuration(
                          intervalToDuration({ start: 0, end: deviceDetail.periodInSeconds * 1000 })
                        )
                      : ''}
                  </div>
                </Fragment>
              );
            case 'status':
              return (
                <Fragment key={deviceDetail.label}>
                  <div className="font-bold">{deviceDetail.label}</div>
                  <div>
                    <StatusChip value={deviceDetail.value} config={deviceDetail.config} />
                  </div>
                </Fragment>
              );
            case 'input':
              return (
                <Fragment key={deviceDetail.label}>
                  <DeviceDetailInput
                    label={deviceDetail.label}
                    value={deviceDetail.value}
                    onChange={deviceDetail.onValueChange}
                    requiresConfirmation={deviceDetail.requiresConfirmation}
                  />
                </Fragment>
              );
            case 'select':
              return (
                <Fragment key={deviceDetail.label}>
                  <div className="font-bold">{deviceDetail.label}</div>
                  <div>
                    <Select
                      value={deviceDetail.value}
                      variant="standard"
                      disableUnderline
                      size="small"
                      onChange={deviceDetail.onValueChange}
                    >
                      {deviceDetail.options.map((option: SelectOption) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                </Fragment>
              );
          }
        })}
      </div>
      <div className="grid grid-cols-3 gap-4 mt-4 text-left">
        {linksToDisplay.map(({ linkText, linkUrl }: { linkText: string; linkUrl: string }) => (
          <Link to={linkUrl} className="text-blue-500 font-bold underline" key={linkText}>
            {linkText}
          </Link>
        ))}
      </div>
    </div>
  );
};

interface DeviceDetailsProps {
  device: Device;
  deviceInitialConfig: DeviceInitialConfig;
  icons: DeviceIconDto[];
  timezones: Set<string>;
  onDeviceIconChange: (iconId: number) => void;
  onDeviceReplacementNumberChange: (replacementNumber: string | null) => void;
  onDeviceTimezoneChange: (timezone: string | null) => void;
}

interface LinksToDisplay {
  linkText: string;
  linkUrl: string;
}

interface DeviceDetailBase {
  type: 'text' | 'period' | 'input' | 'status' | 'select';
  label: string;
}

interface TextDetail extends DeviceDetailBase {
  type: 'text';
  value: string;
}

interface DurationDetail extends DeviceDetailBase {
  type: 'period';
  periodInSeconds: number;
}

interface InputDetail extends DeviceDetailBase {
  type: 'input';
  label: string;
  value: string | null;
  onValueChange: (value: string | null) => void;
  requiresConfirmation?: boolean; //default -  false
}

interface StatusDetail extends DeviceDetailBase {
  type: 'status';
  value: string;
  config: StatusChipConfig;
}

interface SelectOption {
  value: string;
  label: ReactNode;
}

interface SelectDetail extends DeviceDetailBase {
  type: 'select';
  value: string;
  options: SelectOption[];
  onValueChange: (event: SelectChangeEvent) => void;
}

type DeviceDetail = TextDetail | DurationDetail | InputDetail | StatusDetail | SelectDetail;

export default DeviceDetails;
